/**
 * CustomerListPage.js - 客户管理页
 * 支持搜索客户、查看列表、跳转到客户详情页
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Table,
  Button,
  Input,
  Select,
  Space,
  Tag,
  Popconfirm,
  message,
  Typography,
  Tooltip,
  Row,
  Col,
} from 'antd';
import {
  SearchOutlined,
  StopOutlined,
  CheckCircleOutlined,
  ImportOutlined,
  ExportOutlined,
} from '@ant-design/icons';
import { adminUserApi } from '../../api/index';
import { DatePicker } from 'antd';

const { Title } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

export default function CustomerListPage() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  // 搜索条件
  const [keyword, setKeyword] = useState('');
  const [role, setRole] = useState(undefined);
  const [lastLoginFrom, setLastLoginFrom] = useState('');
  const [lastLoginTo, setLastLoginTo] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);

  // 拉取客户列表
  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminUserApi.list({
        keyword: keyword || undefined,
        role: role || undefined,
        lastLoginFrom: lastLoginFrom || undefined,
        lastLoginTo: lastLoginTo || undefined,
        page,
        pageSize,
      });
      if (Array.isArray(data)) {
        setCustomers(data);
        setTotal(data.length);
      } else {
        setCustomers(data?.list || data?.items || []);
        setTotal(data?.total || 0);
      }
    } catch (err) {
      message.error('加载客户列表失败：' + (err.message || '未知错误'));
    } finally {
      setLoading(false);
    }
  }, [keyword, role, lastLoginFrom, lastLoginTo, page, pageSize]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  // 搜索
  const handleSearch = () => {
    setPage(1);
    fetchCustomers();
  };

  // 快速切换启用/禁用状态
  const handleToggleEnabled = async (user) => {
    try {
      await adminUserApi.update(user.id, { enabled: !user.enabled });
      message.success(user.enabled ? '用户已禁用' : '用户已启用');
      fetchCustomers();
    } catch (err) {
      message.error('操作失败：' + (err.message || '未知错误'));
    }
  };

  // 导出 CSV
  const handleExportCsv = async () => {
    try {
      const blob = await adminUserApi.export();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `customers_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
      message.success('导出成功');
    } catch (err) {
      message.error('导出失败：' + (err.message || '未知错误'));
    }
  };

  // 导入 CSV
  const handleImportCsv = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.csv';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      // 检查文件大小（限制 5MB）
      if (file.size > 5 * 1024 * 1024) {
        message.error('文件大小不能超过 5MB');
        return;
      }

      // 显示进度条
      const hideLoading = message.loading({
        content: '正在导入客户数据，请稍候...',
        duration: 0,
      });

      try {
        const formData = new FormData();
        formData.append('file', file);
        const result = await adminUserApi.import(formData);
        hideLoading();

        // 显示导入结果
        const imported = result?.imported || 0;
        const skipped = result?.skipped || 0;
        message.success({
          content: `导入完成：成功导入 ${imported} 条，跳过 ${skipped} 条（已存在）`,
          duration: 5,
        });
        fetchCustomers();
      } catch (err) {
        hideLoading();
        message.error('导入失败：' + (err.response?.data?.message || err.message || '未知错误'));
      }
    };
    input.click();
  };

  // 表格列配置
  const columns = [
    {
      title: '邮箱',
      dataIndex: 'email',
      key: 'email',
      ellipsis: true,
      render: (email) => <strong>{email}</strong>,
    },
    {
      title: '姓名',
      key: 'name',
      render: (_, record) =>
        [record.lastName, record.firstName].filter(Boolean).join(' ') || '-',
    },
    {
      title: '角色',
      dataIndex: 'role',
      key: 'role',
      width: 100,
      render: (r) => (
        <Tag color={r === 'admin' ? 'blue' : 'default'}>
          {r === 'admin' ? '管理员' : '普通用户'}
        </Tag>
      ),
    },
    {
      title: '标签',
      dataIndex: 'tags',
      key: 'tags',
      ellipsis: true,
      render: (tags) => (
        <span style={{ fontSize: 12, color: '#666' }}>{tags || '-'}</span>
      ),
    },
    {
      title: '最后登录',
      dataIndex: 'lastLoginAt',
      key: 'lastLoginAt',
      width: 160,
      render: (val) =>
        val ? new Date(val).toLocaleString('zh-CN', { hour12: false }) : '尚未登录',
    },
    {
      title: '注册时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      render: (val) =>
        val ? new Date(val).toLocaleString('zh-CN', { hour12: false }) : '-',
    },
    {
      title: '状态',
      dataIndex: 'enabled',
      key: 'enabled',
      width: 90,
      render: (enabled) => (
        <Tag color={enabled !== false ? 'green' : 'red'}>
          {enabled !== false ? '正常' : '已禁用'}
        </Tag>
      ),
    },
    {
      title: '操作',
      key: 'actions',
      width: 100,
      fixed: 'right',
      render: (_, record) => (
        <Tooltip title={record.enabled !== false ? '禁用账户' : '启用账户'}>
          <Popconfirm
            title={
              record.enabled !== false
                ? '确认禁用该用户？'
                : '确认启用该用户？'
            }
            onConfirm={() => handleToggleEnabled(record)}
            okText="确认"
            cancelText="取消"
          >
            <Button
              type="link"
              size="small"
              danger={record.enabled !== false}
              icon={
                record.enabled !== false ? (
                  <StopOutlined />
                ) : (
                  <CheckCircleOutlined />
                )
              }
              onClick={(e) => e.stopPropagation()}
            />
          </Popconfirm>
        </Tooltip>
      ),
    },
  ];

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <Title level={4} style={{ marginBottom: 0, marginTop: 0 }}>
          客户管理
        </Title>
        <Space>
          <Button icon={<ExportOutlined />} onClick={handleExportCsv}>
            导出 CSV
          </Button>
          <Button icon={<ImportOutlined />} onClick={handleImportCsv}>
            导入 CSV
          </Button>
        </Space>
      </div>

      <Card style={{ borderRadius: 8 }}>
        {/* 搜索筛选栏 */}
        <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} md={8}>
            <Input
              placeholder="搜索邮箱或姓名"
              prefix={<SearchOutlined />}
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              onPressEnter={handleSearch}
              allowClear
            />
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Select
              placeholder="角色筛选"
              value={role}
              onChange={(v) => {
                setRole(v);
                setPage(1);
              }}
              allowClear
              style={{ width: '100%' }}
            >
              <Option value="admin">管理员</Option>
              <Option value="customer">普通用户</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <RangePicker
              value={[lastLoginFrom ? new Date(lastLoginFrom) : null, lastLoginTo ? new Date(lastLoginTo) : null]}
              onChange={(dates) => {
                setLastLoginFrom(dates?.[0] ? dates[0].toISOString().split('T')[0] : '');
                setLastLoginTo(dates?.[1] ? dates[1].toISOString().split('T')[0] : '');
                setPage(1);
              }}
              style={{ width: '100%' }}
              placeholder={['最后上线开始日期', '最后上线结束日期']}
            />
          </Col>
          <Col xs={12} sm={4}>
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={handleSearch}
            >
              搜索
            </Button>
          </Col>
        </Row>

        <Table
          dataSource={customers}
          columns={columns}
          rowKey="id"
          loading={loading}
          scroll={{ x: 800 }}
          onRow={(record) => ({
            onClick: () => navigate(`/admin/customers/${record.id}`),
            style: { cursor: 'pointer' },
          })}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: false,
            showTotal: (t) => `共 ${t} 条`,
            onChange: (p) => setPage(p),
          }}
          locale={{ emptyText: '暂无客户数据' }}
        />
      </Card>
    </div>
  );
}